import { zodResolver } from '@hookform/resolvers/zod';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import SaveIcon from '@mui/icons-material/Save';

import { Stack } from '@mui/material';

import React, { useContext, useState } from 'react';

import { useForm } from 'react-hook-form';

import { ApiContext } from '_lib/api/apiContext';
import { BadRequestError } from '_lib/api/apiErrors';
import { PointmanCompany, PointmanCustomerDetailDTO, PointmanCustomerUpdateSchema } from '_lib/api/apiTypes.shim';
import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { Chip } from '_lib/components/Chip/Chip';
import { ErrorDisplay } from '_lib/components/ErrorDisplay/ErrorDisplay';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { MaskedPhoneDisplay } from '_lib/components/MaskedPhoneDisplay/MaskedPhoneDisplay';
import { TabPanel } from '_lib/components/TabPanel/TabPanel';
import { Typography } from '_lib/components/Typography/Typography';
import { CustomerPhoneCallHistory } from '_lib/featureComponents/CustomerPhoneCallHistory/CustomerPhoneCallHistory';
import { HeaderBox } from '_lib/featureComponents/HeaderBox/HeaderBox';
import { HeaderTextBlock } from '_lib/featureComponents/HeaderTextBlock/HeaderTextBlock';
import { TextBlockTypography } from '_lib/featureComponents/TextBoxTypography/TextBoxTypography';
import {
  PrimaryHeader,
  PrimaryHeaderIconButton,
  StyledDivider,
  StyledTab,
  StyledTabs
} from '_lib/styles/PhoneCall.styled';
import { PhoneCallURLState } from '_lib/types/PhoneCall.types';
import { customerDisplayName, formatAddressTwoLine, formatPhone } from '_lib/utils/helpers';

import { apiSubmitHandler } from '_lib/utils/hookFormUtils';

import { CustomerForm } from '../CustomerForm/CustomerForm';

type CustomerDetailProps = {
  callerId: PhoneCallURLState['callerId'];
  company: PointmanCompany;
  customer: PointmanCustomerDetailDTO;
  onSetCustomer: (customer: PointmanCustomerDetailDTO | null) => void;
};

const normalizeCustomer = (customer: PointmanCustomerDetailDTO) => ({
  ...customer,
  companyName: customer.companyName === null ? undefined : customer.companyName,
  phoneNumber: customer.phoneNumber === null ? undefined : customer.phoneNumber,
  firstName: customer.firstName === null ? undefined : customer.firstName,
  lastName: customer.lastName === null ? undefined : customer.lastName
});

export const CustomerDetail = (props: CustomerDetailProps) => {
  const { callerId, company, customer, onSetCustomer } = props;

  const api = useContext(ApiContext);

  const customerPutMutation = api.pointmanCustomers.usePut();

  const [tabIndex, setTabIndex] = useState(0);
  const [customerBeingEdited, setCustomerBeingEdited] = useState(false);

  const [validationErrors, setValidationErrors] = useState<string[]>();

  const customerUpdateFormContext = useForm({
    mode: 'onChange',
    shouldFocusError: true,
    resolver: zodResolver(PointmanCustomerUpdateSchema),
    defaultValues: customer.isCommercial
      ? {
          ...normalizeCustomer(customer),
          isCommercial: true
        }
      : {
          ...normalizeCustomer(customer),
          isCommercial: false
        }
  });

  const handleSave = apiSubmitHandler(
    customerUpdateFormContext.handleSubmit,
    customerUpdateFormContext.setError,
    async (data, { setFormErrors }) => {
      setValidationErrors(undefined);
      try {
        const updatedCustomer = await customerPutMutation.put({ id: customer.id, data });
        onSetCustomer(updatedCustomer);
        setCustomerBeingEdited(false);
      } catch (err) {
        if (err instanceof BadRequestError) {
          setValidationErrors(err.responseData?.nonFieldErrors);
          setFormErrors(err);
        }
      }
    }
  );

  return (
    <>
      <PrimaryHeader sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <FlexBox sx={{ flexDirection: 'row', justifyContent: 'space-between', paddingX: 1.5, paddingY: 1 }}>
          <Stack useFlexGap sx={{ alignItems: 'flex-start' }}>
            <Typography variant='h3' text={customerDisplayName(customer)} />
            <MaskedPhoneDisplay variant='h3' preText='CID: ' text={callerId ?? 'Invalid Caller ID'} />
          </Stack>
          <Stack direction='row' sx={{ justifyContent: 'flex-end', alignItems: 'baseline' }}>
            {customerBeingEdited ? (
              <PrimaryHeaderIconButton
                onClick={() => {
                  handleSave();
                }}
                disabled={customerPutMutation.isPending}>
                <SaveIcon />
              </PrimaryHeaderIconButton>
            ) : (
              <PrimaryHeaderIconButton
                onClick={() => {
                  setTabIndex(0);
                  setCustomerBeingEdited(true);
                }}
                disabled={customerPutMutation.isPending}>
                <EditIcon />
              </PrimaryHeaderIconButton>
            )}
            <PrimaryHeaderIconButton
              onClick={() => {
                if (customerBeingEdited) setCustomerBeingEdited(false);
                else onSetCustomer(null);
              }}
              disabled={customerPutMutation.isPending}>
              <CloseIcon />
            </PrimaryHeaderIconButton>
          </Stack>
        </FlexBox>
        <StyledDivider />
        <StyledTabs
          value={tabIndex}
          onChange={(_event: React.SyntheticEvent, newValue: number) => setTabIndex(newValue)}>
          {customerBeingEdited ? (
            <StyledTab label='EDIT CUSTOMER' />
          ) : (
            [<StyledTab label='SUMMARY' key={0} />, <StyledTab label='CALL HISTORY' key={1} />]
          )}
        </StyledTabs>
      </PrimaryHeader>
      <TabPanel currentIndex={tabIndex} index={0} sx={{ overflow: 'auto' }}>
        <FlexBox
          sx={(theme) => ({
            backgroundColor: theme.palette.background.default,
            flexDirection: 'column',
            height: '100%',
            width: '100%',
            position: 'relative'
          })}>
          <BackdropSpinner open={customerPutMutation.isPending} />
          {validationErrors?.length && <ErrorDisplay errors={validationErrors} />}
          {customerBeingEdited ? (
            <CustomerForm customerFormContext={customerUpdateFormContext} />
          ) : (
            <Stack sx={{ padding: 2 }} spacing={3} useFlexGap>
              <Stack spacing={0.5}>
                <Typography
                  variant='h3'
                  text={
                    customer.isCommercial
                      ? customer.companyName || 'Unknown Company'
                      : customer.fullName || 'Unknown Customer'
                  }
                />
                {customer.isCommercial && customer.fullName && <Typography text={customer.fullName} />}
                {!customer.isCommercial && customer.companyName && <Typography text={customer.companyName} />}
                <Typography
                  text={formatAddressTwoLine(customer, { includeCountry: true })}
                  sx={{ whiteSpace: 'pre-line' }}
                />
                {(!!customer.phoneNumber || !!customer.email) && (
                  <Stack direction='row'>
                    {!!customer.phoneNumber && (
                      <Chip
                        label={formatPhone(customer.phoneNumber)}
                        size='small'
                        icon={<PhoneIcon fontSize='small' />}
                      />
                    )}
                    {!!customer.email && (
                      <Chip label={customer.email} size='small' icon={<EmailIcon fontSize='small' />} />
                    )}
                  </Stack>
                )}
              </Stack>
              <HeaderBox headerText='Secondary Contact'>
                {(!customer.secondaryContact ||
                  (!customer.secondaryContact.name && !customer.secondaryContact.phoneNumber)) && (
                  <Typography text='N/A' />
                )}
                {customer.secondaryContact?.name && <Typography text={customer.secondaryContact.name} />}
                {customer.secondaryContact?.phoneNumber && (
                  <Chip
                    label={formatPhone(customer.secondaryContact.phoneNumber)}
                    size='small'
                    icon={<PhoneIcon fontSize='small' />}
                    sx={{ width: 'fit-content' }}
                  />
                )}
              </HeaderBox>
              <HeaderTextBlock
                headerText='Ownership'
                text={customer.isOwner ? 'Property Owner' : 'Not Property Owner'}
              />
              <HeaderTextBlock headerText='Lead Source Indicator' text={customer.leadSourceIndicator || 'N/A'} />
              <HeaderBox headerText='Billing Contact'>
                {!customer.billingContact ? (
                  <Typography text='N/A' />
                ) : (
                  <Stack useFlexGap spacing={0}>
                    <Typography text={customer.billingContact.fullName || 'Unknown Name'} />
                    <TextBlockTypography text={formatAddressTwoLine(customer.billingContact)} />
                    <Stack direction='row'>
                      {customer.billingContact.phoneNumber && (
                        <Chip
                          label={formatPhone(customer.billingContact.phoneNumber)}
                          size='small'
                          icon={<PhoneIcon fontSize='small' />}
                        />
                      )}
                      {customer.billingContact.email && (
                        <Chip
                          label={customer.billingContact.email}
                          size='small'
                          icon={<EmailIcon fontSize='small' />}
                        />
                      )}
                    </Stack>
                  </Stack>
                )}
              </HeaderBox>
            </Stack>
          )}
        </FlexBox>
      </TabPanel>
      <TabPanel currentIndex={tabIndex} index={1} sx={{ overflow: 'auto' }}>
        <CustomerPhoneCallHistory company={company} customerId={customer.id} />
      </TabPanel>
    </>
  );
};
